/**
 * WebGL capability probe.
 *
 * Deliberately in its own module with NO three.js import, so the shell can ask
 * "should I reserve space for a viewport at all?" without pulling ~519 kB into
 * the entry chunk. Importing this is free; importing the viewer is not.
 *
 * Some managed school Chromebooks disable WebGL outright, and a meaningful
 * share of this audience is on them. The 2D editor must remain fully usable
 * when this returns false — the viewport is an enhancement, never a dependency.
 */

let cached: boolean | null = null;

export function webglSupported(): boolean {
  if (cached !== null) return cached;
  if (typeof document === "undefined") {
    cached = false;
    return cached;
  }

  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ??
      canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl");
    cached = Boolean(gl);

    // Release the probe context immediately. Browsers cap simultaneous WebGL
    // contexts (often ~16), and a leaked one costs a slot the real viewer needs.
    if (gl && "getExtension" in gl) {
      (gl as WebGLRenderingContext).getExtension("WEBGL_lose_context")?.loseContext();
    }
  } catch {
    cached = false;
  }
  return cached;
}

/**
 * Rough device-capability hint for choosing an initial pixel ratio.
 * Not a benchmark — the viewer adapts from measured frame time after the first
 * few frames. This only avoids opening at 2x on hardware that clearly cannot.
 */
export function initialPixelRatio(): number {
  if (typeof window === "undefined") return 1;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cores = navigator.hardwareConcurrency ?? 4;
  const memory = (navigator as { deviceMemory?: number }).deviceMemory ?? 4;
  return cores <= 4 || memory <= 4 ? 1 : dpr;
}

/**
 * How many pixels the viewer may put in its drawing buffer.
 *
 * Capping devicePixelRatio at 2 is not a cap on work — it is a cap on a
 * *multiplier*. The pane it multiplies grows with the window, so the actual
 * pixel count did not stop anywhere:
 *
 *     1280x800  @1x   0.33 MP     16.9 ms/frame,  1% janky
 *     1280x800  @2x   1.08 MP     16.9 ms/frame,  1% janky
 *     1920x1080 @2x   2.47 MP     26.0 ms/frame, 48% janky
 *     2560x1440 @2x   4.76 MP
 *     3440x1440 @2x   6.44 MP      19x the first row
 *
 * Draw calls were identical (726) at every size, so none of that is scene
 * complexity — it is fill rate, and a larger, sharper monitor was pure added
 * cost with nothing bounding it. The homepage escaped only by accident: its
 * preview sits in a fixed 512x489 card, so it lands at 1.00 MP on any display.
 *
 * 2 MP is roughly a 1440p frame. Chosen so that panes up to about 1000x1000 CSS
 * pixels still get the full 2x — the sizes where sharpness is visible and the
 * cost is affordable — and only larger ones give ratio back.
 */
export const VIEWER_PIXEL_BUDGET = 2_000_000;

/**
 * The pixel ratio to render this pane at.
 *
 * `scale` is a temporary multiplier for interaction: painting re-renders the
 * avatar on every frame, and a stroke is the one moment when responsiveness is
 * worth more than resolution. It floors at 0.5 so the preview degrades to soft
 * rather than to blocks.
 */
export function pixelRatioFor(cssWidth: number, cssHeight: number, scale = 1): number {
  const area = Math.max(1, cssWidth * cssHeight);
  // sqrt: the budget is an area, the ratio applies to both axes.
  const affordable = Math.sqrt(VIEWER_PIXEL_BUDGET / area);
  const ratio = Math.min(initialPixelRatio(), affordable) * scale;
  return Math.max(0.5, ratio);
}
